import { SearchOutlined } from '@ant-design/icons';
import { history, useAccess, useLocation } from '@umijs/max';
import { Empty, Input, List, Modal, Tag, Typography } from 'antd';
import type { InputRef } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildCommandPaletteItems,
  COMMAND_PALETTE_OPEN_EVENT,
  commandPaletteShortcutText,
  filterCommandPaletteItems,
  type CommandPaletteItem,
} from '@/utils/commandPalette';

const { Text } = Typography;

const itemButtonStyle: React.CSSProperties = {
  width: '100%',
  border: 0,
  background: 'transparent',
  padding: 0,
  textAlign: 'left',
  cursor: 'pointer',
};

const CommandPalette: React.FC = () => {
  const access = useAccess();
  const location = useLocation();
  const inputRef = useRef<InputRef>(null);
  const shortcutText = useMemo(() => commandPaletteShortcutText(), []);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const allItems = useMemo(
    () =>
      buildCommandPaletteItems(access, {
        currentPathname: location.pathname,
        currentSearch: location.search,
      }),
    [access, location.pathname, location.search],
  );

  const items = useMemo(
    () => filterCommandPaletteItems(allItems, keyword),
    [allItems, keyword],
  );

  useEffect(() => {
    const openPalette = () => setOpen(true);
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openPalette();
      }
    };
    window.addEventListener(COMMAND_PALETTE_OPEN_EVENT, openPalette);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener(COMMAND_PALETTE_OPEN_EVENT, openPalette);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [keyword]);

  useEffect(() => {
    if (activeIndex > Math.max(items.length - 1, 0)) {
      setActiveIndex(0);
    }
  }, [activeIndex, items.length]);

  const close = () => {
    setOpen(false);
    setKeyword('');
    setActiveIndex(0);
  };

  const jumpTo = (item?: CommandPaletteItem) => {
    if (!item) return;
    close();
    history.push(item.href);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) =>
        items.length ? Math.min(current + 1, items.length - 1) : 0,
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      jumpTo(items[activeIndex]);
    }
  };

  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      width={640}
      centered
      destroyOnClose
      onCancel={close}
      styles={{
        content: { padding: 0, overflow: 'hidden' },
        body: { padding: 0 },
      }}
    >
      <div style={{ padding: 14, borderBottom: '1px solid #f0f0f0' }}>
        <Input
          ref={inputRef}
          size="large"
          allowClear
          prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
          aria-label="搜索菜单和页面标签"
          placeholder="搜索菜单 / Tab / 拼音 / 首字母"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          onKeyDown={handleInputKeyDown}
          suffix={<Text type="secondary">{shortcutText}</Text>}
        />
      </div>
      <div style={{ maxHeight: 420, overflowY: 'auto', padding: '8px 8px 10px' }}>
        {items.length ? (
          <List
            dataSource={items}
            renderItem={(item, index) => {
              const active = index === activeIndex;
              return (
                <List.Item style={{ padding: 0, borderBlockEnd: 0 }}>
                  <button
                    type="button"
                    style={itemButtonStyle}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => jumpTo(item)}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        minHeight: 52,
                        borderRadius: 8,
                        padding: '8px 10px',
                        background: active ? '#f5f7ff' : 'transparent',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            minWidth: 0,
                          }}
                        >
                          <Text strong ellipsis style={{ maxWidth: 340 }}>
                            {item.title}
                          </Text>
                          <Tag color={item.type === 'tab' ? 'blue' : 'default'}>
                            {item.type === 'tab' ? 'Tab' : '菜单'}
                          </Tag>
                        </div>
                        <Text type="secondary" ellipsis style={{ maxWidth: 500 }}>
                          {[...item.breadcrumb, item.href].join(' / ')}
                        </Text>
                      </div>
                      {active ? <Text type="secondary">Enter</Text> : null}
                    </div>
                  </button>
                </List.Item>
              );
            }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="没有匹配的菜单或 Tab"
            style={{ margin: '48px 0' }}
          />
        )}
      </div>
    </Modal>
  );
};

export default CommandPalette;
